import React, { useEffect, useRef, useState } from 'react';

/**
 * Global animated infrastructure background.
 * - SVG grid of soft gold lines.
 * - Drifting connection nodes.
 * - Subtle data signals traveling between fixed edges.
 * - Cursor-reactive radial light.
 * - Respects prefers-reduced-motion.
 *
 * Rendered `position: fixed` behind all content with pointer-events: none.
 */
const NODE_COUNT = 14;
const rand = (min, max) => Math.random() * (max - min) + min;

const InfrastructureBackground = () => {
  const wrapRef = useRef(null);
  const [reduced, setReduced] = useState(false);
  const [nodes] = useState(() =>
    Array.from({ length: NODE_COUNT }).map(() => ({
      x: rand(6, 94),
      y: rand(8, 92),
      r: rand(1.4, 2.8),
      driftX: rand(-0.02, 0.02),
      driftY: rand(-0.015, 0.015),
      phase: rand(0, Math.PI * 2),
    }))
  );

  // Pairs used for connection lines — fixed at mount so it doesn't jitter.
  const [pairs] = useState(() => {
    const out = [];
    for (let i = 0; i < NODE_COUNT; i++) {
      const j = (i + 1 + Math.floor(rand(0, 3))) % NODE_COUNT;
      out.push([i, j]);
    }
    return out.slice(0, 10);
  });

  useEffect(() => {
    const m = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduced(m.matches);
    const on = () => setReduced(m.matches);
    m.addEventListener?.('change', on);
    return () => m.removeEventListener?.('change', on);
  }, []);

  // Cursor-reactive radial light — uses CSS vars for smooth GPU compositing.
  useEffect(() => {
    if (reduced) return;
    const el = wrapRef.current;
    if (!el) return;
    let raf = null;
    let tx = 50, ty = 40;
    const onMove = (e) => {
      tx = (e.clientX / window.innerWidth) * 100;
      ty = (e.clientY / window.innerHeight) * 100;
      if (raf) return;
      raf = requestAnimationFrame(() => {
        el.style.setProperty('--cx', `${tx}%`);
        el.style.setProperty('--cy', `${ty}%`);
        raf = null;
      });
    };
    window.addEventListener('pointermove', onMove, { passive: true });
    return () => {
      window.removeEventListener('pointermove', onMove);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [reduced]);

  return (
    <div
      ref={wrapRef}
      className="infra-bg"
      aria-hidden="true"
      style={{ '--cx': '50%', '--cy': '30%' }}
    >
      {/* Base gold grid */}
      <div className="infra-grid" />

      {/* Radial cursor-reactive light */}
      <div className="infra-cursor-light" />

      {/* Node network */}
      <svg className="infra-svg" viewBox="0 0 100 100" preserveAspectRatio="none">
        {/* Connection lines with animated dash flow */}
        {pairs.map(([a, b], i) => {
          const na = nodes[a];
          const nb = nodes[b];
          return (
            <line
              key={`l-${i}`}
              x1={na.x}
              y1={na.y}
              x2={nb.x}
              y2={nb.y}
              stroke="rgba(212,178,106,0.14)"
              strokeWidth="0.08"
              strokeDasharray="0.7 1.4"
              vectorEffect="non-scaling-stroke"
            >
              {!reduced && (
                <animate
                  attributeName="stroke-dashoffset"
                  from="0"
                  to="-6"
                  dur={`${8 + (i % 4)}s`}
                  repeatCount="indefinite"
                />
              )}
            </line>
          );
        })}

        {/* Nodes */}
        {nodes.map((n, i) => (
          <g key={`n-${i}`}>
            <circle
              cx={n.x}
              cy={n.y}
              r={n.r / 6}
              fill="rgba(212,178,106,0.55)"
            >
              {!reduced && (
                <animate
                  attributeName="opacity"
                  values="0.35;0.8;0.35"
                  dur={`${3 + (i % 3)}s`}
                  repeatCount="indefinite"
                />
              )}
            </circle>
          </g>
        ))}

        {/* Data signal packets traveling along a couple of edges */}
        {!reduced && pairs.slice(0, 5).map(([a, b], i) => {
          const na = nodes[a];
          const nb = nodes[b];
          return (
            <circle
              key={`p-${i}`}
              r="0.35"
              fill="rgba(239,212,138,0.9)"
            >
              <animate
                attributeName="cx"
                values={`${na.x};${nb.x}`}
                dur={`${6 + i}s`}
                repeatCount="indefinite"
              />
              <animate
                attributeName="cy"
                values={`${na.y};${nb.y}`}
                dur={`${6 + i}s`}
                repeatCount="indefinite"
              />
              <animate
                attributeName="opacity"
                values="0;1;1;0"
                keyTimes="0;0.15;0.85;1"
                dur={`${6 + i}s`}
                repeatCount="indefinite"
              />
            </circle>
          );
        })}
      </svg>

      {/* Legacy grain overlay for filmic depth */}
      <div className="infra-grain" />
    </div>
  );
};

export default InfrastructureBackground;
